import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AiDisposition,
  AiJob,
  CorpusItem,
  CorpusItemImportResult,
  CorpusItemVersion,
  CrosswalkMapping,
  EvidenceFile,
  Paginated,
  Standard,
  StandardVersion,
  StandardVersionDetail,
} from '@grc/types';
import { versionLabel } from '@grc/types';
import { parseDocumentVersion } from '@grc/markdown';
import { apiFetch, apiUrl, ApiRequestError } from '../../lib/api.js';
import { useOrg } from '../../lib/auth.js';
import {
  Badge,
  Button,
  Card,
  Dialog,
  Field,
  Input,
  LoadingPane,
  PageHeader,
  Select,
  Spinner,
  Tabs,
  Textarea,
  formatBytes,
  formatDate,
  formatDateTime,
  useToast,
} from '../../components/ui.js';
import { Markdown } from '../../components/Markdown.js';
import { BackToTop, DocumentOutline, hasOutline } from './DocumentOutline.js';
import { PolicyAcceptanceCard } from '../policies/PolicyAcceptanceCard.js';
import './corpus.css';
import '../import/import.css';

type VersionSummary = Omit<CorpusItemVersion, 'contentMarkdown'>;

type EvidenceUploadStatus = 'queued' | 'uploading' | 'uploaded' | 'failed';

/** Mirrors EVIDENCE_ALLOWED_TYPES (packages/types) by extension. */
const EVIDENCE_ACCEPT =
  '.pdf,.png,.jpg,.jpeg,.heic,.doc,.docx,.xlsx,.pptx,.msg,.csv,.txt,.md,.html,.htm,.zip';

interface EvidenceUpload {
  key: string;
  file: File;
  status: EvidenceUploadStatus;
  error?: string;
}

/** Simple write/preview markdown control (independent of the response editor). */
function ContentEditor({
  value,
  onChange,
  actions,
}: {
  value: string;
  onChange: (v: string) => void;
  /** Rendered alongside the Write/Preview tabs (e.g. the AI Draft button). */
  actions?: ReactNode;
}) {
  const [tab, setTab] = useState<'write' | 'preview'>('write');
  return (
    <div>
      <div className="corpus-editor-toolbar">
        <Tabs
          tabs={[
            { key: 'write' as const, label: 'Write' },
            { key: 'preview' as const, label: 'Preview' },
          ]}
          active={tab}
          onChange={setTab}
        />
        {actions && <div className="corpus-editor-actions">{actions}</div>}
      </div>
      {tab === 'write' ? (
        <Textarea
          aria-label="Content (markdown)"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={{ minHeight: 320, fontFamily: 'var(--font-mono)', fontSize: 13 }}
        />
      ) : (
        <div style={{ border: '1px solid var(--color-border)', borderRadius: 8, padding: 16, minHeight: 320 }}>
          <Markdown markdown={value || '_Nothing to preview._'} />
        </div>
      )}
    </div>
  );
}

export function CorpusItemPage() {
  const { corpusItemId = '' } = useParams();
  const { org } = useOrg();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const canWrite = org.role === 'admin' || org.role === 'contributor';
  // Organisation AI switch: hides the AI Draft action and the AI-driven
  // blank-document dropzone (the server refuses both with 403 ai_disabled).
  const aiEnabled = org.aiEnabled !== false;

  const item = useQuery({
    queryKey: ['corpus-item', corpusItemId],
    queryFn: () => apiFetch<CorpusItem>(`/corpus/${corpusItemId}`),
  });
  const versions = useQuery({
    queryKey: ['corpus-versions', corpusItemId, item.data?.currentVersion],
    queryFn: () => apiFetch<VersionSummary[]>(`/corpus/${corpusItemId}/versions`),
    enabled: !!item.data,
  });
  const crosswalk = useQuery({
    queryKey: ['crosswalk', org.orgId],
    queryFn: () => apiFetch<CrosswalkMapping[]>(`/orgs/${org.orgId}/crosswalk`),
  });
  const evidence = useQuery({
    queryKey: ['corpus-evidence', corpusItemId],
    queryFn: () =>
      apiFetch<Paginated<EvidenceFile>>(`/orgs/${org.orgId}/evidence`, {
        query: { corpusItemId, limit: 100 },
      }),
  });
  const standards = useQuery({
    queryKey: ['standards'],
    queryFn: () => apiFetch<Paginated<Standard>>('/standards', { query: { limit: 100 } }),
  });

  const [editing, setEditing] = useState(false);
  const [content, setContent] = useState('');
  const [changeSummary, setChangeSummary] = useState('');
  // The document's own version label. Left as loaded, the server re-reads it
  // from the saved text; edited, it is sent as an explicit override.
  const [documentVersion, setDocumentVersion] = useState('');
  useEffect(() => {
    if (item.data) {
      setContent(item.data.contentMarkdown);
      setDocumentVersion(item.data.documentVersion);
    }
  }, [item.data?.id, item.data?.currentVersion, item.data?.documentVersion]);
  const detectedVersion = useMemo(
    () => (editing && item.data ? parseDocumentVersion(item.data.title, content) : ''),
    [editing, item.data?.title, content],
  );

  // The two-column document layout only applies when an outline will actually
  // render — otherwise the body would be squeezed into the 240px outline track.
  const showOutline = useMemo(
    () => (item.data ? hasOutline(item.data.contentMarkdown) : false),
    [item.data?.contentMarkdown],
  );

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['corpus-item', corpusItemId] });
    void queryClient.invalidateQueries({ queryKey: ['corpus', org.orgId] });
    void queryClient.invalidateQueries({ queryKey: ['crosswalk', org.orgId] });
  };
  const onError = (err: unknown) =>
    toast(err instanceof ApiRequestError ? err.message : 'Action failed', 'error');

  // Blank-document dropzone: populate from an uploaded document, or let AI
  // draft from best practice when nothing relevant is found in it.
  const importInput = useRef<HTMLInputElement>(null);
  const [importDragOver, setImportDragOver] = useState(false);
  const importDoc = useMutation({
    mutationFn: (files: File[]) => {
      const formData = new FormData();
      for (const file of files.slice(0, 5)) formData.append('file', file);
      return apiFetch<CorpusItemImportResult>(`/corpus/${corpusItemId}/import`, {
        method: 'POST',
        formData,
      });
    },
    onSuccess: (result) => {
      toast(
        result.action === 'imported'
          ? `Populated from ${result.files.join(', ')} — review and approve when ready.`
          : 'No relevant material found in your document — drafted from best practice guidelines instead. Review before approving.',
        'success',
      );
      invalidate();
    },
    onError: (err) => {
      if (err instanceof ApiRequestError && err.code === 'not_configured') {
        toast(
          'Document import is not configured on this environment — an administrator needs to set the OpenAI API key first.',
          'error',
        );
        return;
      }
      onError(err);
    },
  });

  const [aiJob, setAiJob] = useState<AiJob | null>(null);
  const update = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiFetch<CorpusItem>(`/corpus/${corpusItemId}`, { method: 'PATCH', body }),
    onSuccess: () => {
      setEditing(false);
      setChangeSummary('');
      setAiJob(null);
      toast('Saved.', 'success');
      invalidate();
    },
    onError,
  });

  // AI draft (admin/contributor only — same gate as editing).
  const aiDraft = useMutation({
    mutationFn: () =>
      apiFetch<AiJob>('/ai/generate', {
        method: 'POST',
        body: { kind: 'draft', corpusItemId },
      }),
    onSuccess: (job) => {
      if (job.outputContent) setAiJob(job);
      else toast('The AI draft came back empty — please try again.', 'error');
    },
    onError: (err) => {
      if (err instanceof ApiRequestError && err.code === 'not_configured') {
        toast(
          'AI drafting is not configured on this environment — an administrator needs to set the OpenAI API key first.',
          'error',
        );
        return;
      }
      onError(err);
    },
  });
  const aiDisposition = useMutation({
    mutationFn: ({ jobId, disposition }: { jobId: string; disposition: AiDisposition }) =>
      apiFetch(`/ai/jobs/${jobId}/disposition`, { method: 'POST', body: { disposition } }),
  });
  const acceptAiDraft = () => {
    if (!aiJob?.outputContent) return;
    setContent(aiJob.outputContent);
    setChangeSummary('Accepted AI draft');
    aiDisposition.mutate({ jobId: aiJob.id, disposition: 'accepted' });
    setAiJob(null);
    toast('AI draft loaded into the editor — review it, then save.', 'success');
  };
  const discardAiDraft = () => {
    if (!aiJob) return;
    aiDisposition.mutate({ jobId: aiJob.id, disposition: 'rejected' });
    setAiJob(null);
  };
  const archive = useMutation({
    mutationFn: () => apiFetch(`/corpus/${corpusItemId}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast('Item archived.', 'success');
      invalidate();
    },
    onError,
  });

  // Crosswalk add dialog
  const [mapOpen, setMapOpen] = useState(false);
  const [mapStandardId, setMapStandardId] = useState('');
  const [mapVersionId, setMapVersionId] = useState('');
  const [mapClauseId, setMapClauseId] = useState('');
  const [mapConfidence, setMapConfidence] = useState<'high' | 'medium' | 'low'>('medium');
  const mapVersions = useQuery({
    queryKey: ['standard-versions', mapStandardId],
    queryFn: () => apiFetch<StandardVersion[]>(`/standards/${mapStandardId}/versions`),
    enabled: !!mapStandardId,
  });
  const mapVersionDetail = useQuery({
    queryKey: ['version-detail', mapVersionId],
    queryFn: () => apiFetch<StandardVersionDetail>(`/standard-versions/${mapVersionId}`),
    enabled: !!mapVersionId,
  });
  const flatClauses: Array<{ id: string; label: string }> = [];
  if (mapVersionDetail.data) {
    const walk = (nodes: StandardVersionDetail['clauses'], depth: number) => {
      for (const n of nodes) {
        flatClauses.push({ id: n.id, label: `${' '.repeat(depth * 2)}${n.clauseNumber} — ${n.title}` });
        walk(n.children, depth + 1);
      }
    };
    walk(mapVersionDetail.data.clauses, 0);
  }
  const addMapping = useMutation({
    mutationFn: () =>
      apiFetch(`/orgs/${org.orgId}/crosswalk`, {
        method: 'POST',
        body: {
          corpusItemId,
          standardId: mapStandardId,
          versionId: mapVersionId || null,
          clauseId: mapClauseId || null,
          confidence: mapConfidence,
        },
      }),
    onSuccess: () => {
      toast('Mapping added — new assessments will pre-fill from this item.', 'success');
      setMapOpen(false);
      setMapClauseId('');
      invalidate();
    },
    onError,
  });
  const removeMapping = useMutation({
    mutationFn: (id: string) => apiFetch(`/crosswalk/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
    onError,
  });

  // Evidence upload — multi-file drag-and-drop, uploaded sequentially.
  const evidenceInput = useRef<HTMLInputElement>(null);
  const [evidenceUploads, setEvidenceUploads] = useState<EvidenceUpload[]>([]);
  const [evidenceDragOver, setEvidenceDragOver] = useState(false);
  const [uploadingEvidence, setUploadingEvidence] = useState(false);

  const addEvidenceFiles = (incoming: FileList | null) => {
    if (!incoming || incoming.length === 0) return;
    setEvidenceUploads((current) => {
      const merged = [...current];
      for (const file of Array.from(incoming)) {
        const key = `${file.name}-${file.size}`;
        if (!merged.some((u) => u.key === key)) merged.push({ key, file, status: 'queued' });
      }
      return merged;
    });
  };
  const patchEvidenceUpload = (key: string, patch: Partial<EvidenceUpload>) =>
    setEvidenceUploads((current) => current.map((u) => (u.key === key ? { ...u, ...patch } : u)));

  const uploadEvidence = async () => {
    const pending = evidenceUploads.filter((u) => u.status === 'queued' || u.status === 'failed');
    if (pending.length === 0 || uploadingEvidence) return;
    setUploadingEvidence(true);
    let uploaded = 0;
    let failed = 0;
    for (const entry of pending) {
      patchEvidenceUpload(entry.key, { status: 'uploading', error: undefined });
      try {
        const formData = new FormData();
        formData.append('file', entry.file);
        await apiFetch(`/corpus/${corpusItemId}/evidence`, { method: 'POST', formData });
        patchEvidenceUpload(entry.key, { status: 'uploaded' });
        uploaded += 1;
      } catch (err) {
        patchEvidenceUpload(entry.key, {
          status: 'failed',
          error: err instanceof ApiRequestError ? err.message : 'Upload failed',
        });
        failed += 1;
      }
    }
    setUploadingEvidence(false);
    if (uploaded > 0) {
      void queryClient.invalidateQueries({ queryKey: ['corpus-evidence', corpusItemId] });
    }
    toast(
      failed === 0
        ? `Uploaded ${uploaded} file${uploaded === 1 ? '' : 's'}.`
        : `Uploaded ${uploaded} of ${pending.length} file${pending.length === 1 ? '' : 's'} — ${failed} failed.`,
      failed === 0 ? 'success' : 'error',
    );
    // Successful uploads now appear in the evidence list; keep failures visible for retry.
    setEvidenceUploads((current) => current.filter((u) => u.status !== 'uploaded'));
  };

  const [viewVersion, setViewVersion] = useState<number | null>(null);
  const viewedVersion = useQuery({
    queryKey: ['corpus-version', corpusItemId, viewVersion],
    queryFn: () =>
      apiFetch<CorpusItemVersion>(`/corpus/${corpusItemId}/versions/${viewVersion}`),
    enabled: viewVersion !== null,
  });

  if (item.isLoading) return <LoadingPane label="Loading corpus item" />;
  if (!item.data) return null;
  const data = item.data;
  const mappings = (crosswalk.data ?? []).filter((m) => m.corpusItemId === corpusItemId);
  const standardById = new Map((standards.data?.items ?? []).map((s) => [s.id, s]));
  const archived = data.status === 'archived';
  const pendingEvidenceCount = evidenceUploads.filter(
    (u) => u.status === 'queued' || u.status === 'failed',
  ).length;

  return (
    <div>
      <p>
        <Link to="/corpus">← Shared corpus</Link>
      </p>
      <PageHeader
        title={data.title}
        subtitle={
          <>
            <Badge>{data.key}</Badge>{' '}
            <Badge tone={data.status === 'approved' ? 'success' : archived ? 'neutral' : 'info'}>
              {data.status}
            </Badge>{' '}
            {data.currentVersion > 0 && <>{versionLabel(data.documentVersion, data.currentVersion)} · </>}updated{' '}
            {formatDate(data.updatedAt)}
          </>
        }
        actions={
          <>
            {data.currentVersion > 0 && (
              <a href={apiUrl(`/corpus/${data.id}/export.pdf`)} title="Download this document as a PDF">
                <Button variant="ghost">Download PDF</Button>
              </a>
            )}
            {canWrite && !archived && (
              <>
                {data.status === 'draft' ? (
                  <Button variant="secondary" onClick={() => update.mutate({ status: 'approved' })}>
                    Mark approved
                  </Button>
                ) : (
                  <Button variant="secondary" onClick={() => update.mutate({ status: 'draft' })}>
                    Back to draft
                  </Button>
                )}
                <Button variant="danger" onClick={() => archive.mutate()}>
                  Archive
                </Button>
              </>
            )}
          </>
        }
      />

      <Card
        title="Content"
        actions={
          canWrite && !archived && !editing ? (
            <Button size="sm" variant="secondary" onClick={() => setEditing(true)}>
              Edit
            </Button>
          ) : undefined
        }
      >
        {editing ? (
          <>
            <ContentEditor
              value={content}
              onChange={setContent}
              actions={
                canWrite && aiEnabled ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    loading={aiDraft.isPending}
                    onClick={() => aiDraft.mutate()}
                  >
                    AI Draft
                  </Button>
                ) : undefined
              }
            />
            {aiDraft.isPending && (
              <p className="corpus-ai-pending" role="status">
                Drafting from this item&rsquo;s context — this can take a moment.
              </p>
            )}
            {aiJob?.outputContent && (
              <div className="corpus-ai-output">
                <div className="corpus-ai-badge-row">
                  <Badge tone="purple">AI draft</Badge>
                  <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)' }}>
                    {aiJob.model} · review before use
                  </span>
                </div>
                <Markdown markdown={aiJob.outputContent} />
                <div className="corpus-ai-actions">
                  <Button size="sm" onClick={acceptAiDraft}>
                    Accept
                  </Button>
                  <Button size="sm" variant="secondary" onClick={discardAiDraft}>
                    Discard
                  </Button>
                </div>
              </div>
            )}
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', marginTop: 16, flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 240px' }}>
                <Field label="Change summary" hint="Recorded in version history">
                  {(props) => (
                    <Input {...props} value={changeSummary} onChange={(e) => setChangeSummary(e.target.value)} />
                  )}
                </Field>
              </div>
              <div style={{ flex: '0 1 200px' }}>
                <Field
                  label="Document version"
                  hint={
                    detectedVersion
                      ? `Found in the document: ${versionLabel(detectedVersion, data.currentVersion)}`
                      : 'Not stated in the document — enter it if known'
                  }
                >
                  {(props) => (
                    <Input
                      {...props}
                      maxLength={40}
                      placeholder={detectedVersion || 'e.g. 3.1.0'}
                      value={documentVersion}
                      onChange={(e) => setDocumentVersion(e.target.value)}
                    />
                  )}
                </Field>
              </div>
              <div style={{ marginBottom: 16, display: 'flex', gap: 8 }}>
                <Button
                  loading={update.isPending}
                  onClick={() =>
                    update.mutate({
                      contentMarkdown: content,
                      changeSummary,
                      // Only an edited label is an override; untouched, the server re-reads the text.
                      ...(documentVersion !== data.documentVersion ? { documentVersion } : {}),
                    })
                  }
                >
                  Save
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setEditing(false);
                    setContent(data.contentMarkdown);
                    setDocumentVersion(data.documentVersion);
                    setAiJob(null);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </>
        ) : data.contentMarkdown ? (
          <div className={showOutline ? 'corpus-doc' : undefined}>
            {showOutline && <DocumentOutline markdown={data.contentMarkdown} />}
            <div className="corpus-doc-content">
              <Markdown markdown={data.contentMarkdown} />
              <BackToTop markdown={data.contentMarkdown} />
            </div>
          </div>
        ) : canWrite && !archived && !aiEnabled ? (
          <>
            <p style={{ color: 'var(--color-text-muted)', marginTop: 0 }}>AI import is turned off in Organisation settings</p>
            <p style={{ color: 'var(--color-text-muted)', marginBottom: 0 }}>
              Use Edit to draft this document by hand — it can then pre-fill assessments.
            </p>
          </>
        ) : canWrite && !archived ? (
          <>
            <div
              className={`import-dropzone ${importDragOver ? 'drag-over' : ''} ${importDoc.isPending ? 'disabled' : ''}`}
              aria-disabled={importDoc.isPending}
              onDragOver={(e) => {
                e.preventDefault();
                if (!importDoc.isPending) setImportDragOver(true);
              }}
              onDragLeave={() => setImportDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setImportDragOver(false);
                if (!importDoc.isPending && e.dataTransfer.files.length > 0) {
                  importDoc.mutate(Array.from(e.dataTransfer.files));
                }
              }}
            >
              <input
                ref={importInput}
                type="file"
                multiple
                accept=".txt,.md,.csv,.docx,.xlsx,.pdf"
                className="sr-only"
                aria-label={`Upload existing content for ${data.title}`}
                disabled={importDoc.isPending}
                onChange={(e) => {
                  if (e.target.files && e.target.files.length > 0) {
                    importDoc.mutate(Array.from(e.target.files));
                  }
                  e.target.value = '';
                }}
              />
              <p className="import-dropzone-hint">
                Drag and drop existing content here, or{' '}
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={importDoc.isPending}
                  onClick={() => importInput.current?.click()}
                >
                  Choose files
                </Button>
              </p>
              <p className="import-dropzone-formats">
                We&rsquo;ll read your document and populate &ldquo;{data.title}&rdquo; from it — if
                nothing relevant is found, AI drafts it from best practice guidelines instead.
              </p>
            </div>
            {importDoc.isPending && (
              <p className="corpus-ai-pending" role="status">
                Reading your document and drafting &ldquo;{data.title}&rdquo; — this can take a
                minute.
              </p>
            )}
            <p style={{ color: 'var(--color-text-muted)', marginBottom: 0 }}>
              Or use Edit to draft this document by hand — it can then pre-fill assessments.
            </p>
          </>
        ) : (
          <p style={{ color: 'var(--color-text-muted)' }}>No content yet.</p>
        )}
      </Card>

      {org.role !== 'assessor' && <PolicyAcceptanceCard item={data} isAdmin={org.role === 'admin'} />}

      <Card
        title="Standard mappings (crosswalk)"
        actions={
          canWrite && !archived ? (
            <Button size="sm" variant="secondary" onClick={() => setMapOpen(true)}>
              Add mapping
            </Button>
          ) : undefined
        }
      >
        {mappings.length === 0 ? (
          <p style={{ color: 'var(--color-text-muted)' }}>
            Map this item to standard clauses so new assessments pre-fill from it (PLAN §5.5).
          </p>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {mappings.map((m) => (
              <li
                key={m.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 12,
                  padding: '8px 0',
                  borderBottom: '1px solid var(--color-border)',
                  alignItems: 'center',
                }}
              >
                <span>
                  {standardById.get(m.standardId)?.name ?? m.standardId}
                  {m.clauseId && <> · clause-level</>}
                  {!m.clauseId && m.versionId && <> · version-level</>}
                  {!m.versionId && <> · standard-level</>} <Badge tone={m.confidence === 'high' ? 'success' : m.confidence === 'low' ? 'warning' : 'neutral'}>{m.confidence}</Badge>
                  {m.requiresReview && <Badge tone="warning">needs review</Badge>}
                </span>
                {canWrite && (
                  <Button size="sm" variant="ghost" onClick={() => removeMapping.mutate(m.id)}>
                    Remove
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card title="Versions">
        {(versions.data ?? []).length === 0 ? (
          <p style={{ color: 'var(--color-text-muted)' }}>No versions yet.</p>
        ) : (
          (versions.data ?? []).map((v) => (
            <div
              key={v.id}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '6px 0',
                borderBottom: '1px solid var(--color-border)',
                alignItems: 'center',
              }}
            >
              <span>
                <strong>{versionLabel(v.documentVersion, v.versionNumber)}</strong>{' '}
                <span style={{ color: 'var(--color-text-faint)', fontSize: 'var(--text-xs)' }}>
                  {v.documentVersion && <>revision {v.versionNumber} · </>}
                  {formatDateTime(v.createdAt)}
                </span>
                {v.changeSummary && (
                  <span style={{ color: 'var(--color-text-muted)' }}> — {v.changeSummary}</span>
                )}
              </span>
              <Button size="sm" variant="ghost" onClick={() => setViewVersion(v.versionNumber)}>
                View
              </Button>
            </div>
          ))
        )}
      </Card>

      <Card title="Attached evidence">
        {(evidence.data?.items ?? []).length === 0 && (
          <p style={{ color: 'var(--color-text-muted)' }}>
            Attach supporting files (org charts, registers, certificates) that travel with this
            document.
          </p>
        )}
        {(evidence.data?.items ?? []).map((e) => (
          <div
            key={e.id}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '6px 0',
              borderBottom: '1px solid var(--color-border)',
            }}
          >
            <span>
              <a href={apiUrl(`/evidence/${e.id}/download`)}>{e.fileName}</a>{' '}
              <span style={{ color: 'var(--color-text-faint)', fontSize: 'var(--text-xs)' }}>
                {formatBytes(e.sizeBytes)} · {formatDate(e.uploadedAt)}
              </span>
            </span>
            <Badge tone={e.retentionStatus === 'active' ? 'success' : 'neutral'}>{e.retentionStatus}</Badge>
          </div>
        ))}
        {canWrite && !archived && (
          <div style={{ marginTop: 12 }}>
            <div
              className={`corpus-dropzone ${evidenceDragOver ? 'drag-over' : ''}`}
              onDragOver={(e) => {
                e.preventDefault();
                setEvidenceDragOver(true);
              }}
              onDragLeave={() => setEvidenceDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setEvidenceDragOver(false);
                addEvidenceFiles(e.dataTransfer.files);
              }}
            >
              <input
                ref={evidenceInput}
                type="file"
                multiple
                accept={EVIDENCE_ACCEPT}
                className="sr-only"
                aria-label="Choose evidence files for this corpus item"
                onChange={(e) => {
                  addEvidenceFiles(e.target.files);
                  e.target.value = '';
                }}
              />
              <p className="corpus-dropzone-hint">
                Drag and drop evidence files here, or{' '}
                <Button variant="secondary" size="sm" onClick={() => evidenceInput.current?.click()}>
                  Choose files
                </Button>
              </p>
              <p className="corpus-dropzone-formats">
                PDF, images, Office documents, Outlook messages, CSV, text, Markdown, HTML or zip — up to 50 MB each
              </p>
            </div>
            {evidenceUploads.length > 0 && (
              <ul className="corpus-upload-list">
                {evidenceUploads.map((u) => (
                  <li key={u.key}>
                    <span className="corpus-upload-name">{u.file.name}</span>
                    <span className="corpus-upload-size">{formatBytes(u.file.size)}</span>
                    {u.status === 'uploading' ? (
                      <span className="corpus-upload-status">
                        <Spinner label={`Uploading ${u.file.name}`} /> uploading
                      </span>
                    ) : u.status === 'uploaded' ? (
                      <Badge tone="success">uploaded</Badge>
                    ) : u.status === 'failed' ? (
                      <span className="corpus-upload-status">
                        <Badge tone="danger">failed</Badge>
                        {u.error && <span className="corpus-upload-error">{u.error}</span>}
                      </span>
                    ) : (
                      <Badge tone="neutral">queued</Badge>
                    )}
                    {!uploadingEvidence && (
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={`Remove ${u.file.name}`}
                        onClick={() =>
                          setEvidenceUploads((current) => current.filter((x) => x.key !== u.key))
                        }
                      >
                        Remove
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {pendingEvidenceCount > 0 && (
              <div style={{ marginTop: 12 }}>
                <Button loading={uploadingEvidence} onClick={() => void uploadEvidence()}>
                  Upload {pendingEvidenceCount} file{pendingEvidenceCount === 1 ? '' : 's'}
                </Button>
              </div>
            )}
          </div>
        )}
      </Card>

      <Dialog open={mapOpen} onClose={() => setMapOpen(false)} title="Map to a standard">
        <Field label="Standard" required>
          {(props) => (
            <Select
              {...props}
              value={mapStandardId}
              onChange={(e) => {
                setMapStandardId(e.target.value);
                setMapVersionId('');
                setMapClauseId('');
              }}
            >
              <option value="">Choose…</option>
              {(standards.data?.items ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Version" hint="Optional — leave blank to map at standard level">
          {(props) => (
            <Select
              {...props}
              disabled={!mapStandardId}
              value={mapVersionId}
              onChange={(e) => {
                setMapVersionId(e.target.value);
                setMapClauseId('');
              }}
            >
              <option value="">Whole standard</option>
              {(mapVersions.data ?? []).map((v) => (
                <option key={v.id} value={v.id}>
                  {v.label}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Clause" hint="Clause-level mappings drive assessment pre-fill">
          {(props) => (
            <Select
              {...props}
              disabled={!mapVersionId}
              value={mapClauseId}
              onChange={(e) => setMapClauseId(e.target.value)}
            >
              <option value="">Whole version</option>
              {flatClauses.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Confidence">
          {(props) => (
            <Select
              {...props}
              value={mapConfidence}
              onChange={(e) => setMapConfidence(e.target.value as typeof mapConfidence)}
            >
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </Select>
          )}
        </Field>
        <div className="dialog-actions">
          <Button variant="secondary" onClick={() => setMapOpen(false)}>
            Cancel
          </Button>
          <Button loading={addMapping.isPending} disabled={!mapStandardId} onClick={() => addMapping.mutate()}>
            Add mapping
          </Button>
        </div>
      </Dialog>

      <Dialog
        open={viewVersion !== null}
        onClose={() => setViewVersion(null)}
        title={
          viewVersion === null
            ? ''
            : versionLabel(
                (versions.data ?? []).find((v) => v.versionNumber === viewVersion)?.documentVersion ?? '',
                viewVersion,
              )
        }
      >
        {viewedVersion.data ? (
          <Markdown markdown={viewedVersion.data.contentMarkdown} />
        ) : (
          <LoadingPane label="Loading version" />
        )}
        <div className="dialog-actions">
          <Button onClick={() => setViewVersion(null)}>Close</Button>
        </div>
      </Dialog>
    </div>
  );
}
